/**
 * Enhanced Table Toolbar Component - Unified Filter Design
 *
 * Layout:
 * - Row 1: search (left); leading content, funnel, columns, actions, Create (right)
 * - Row 2: wrapping filter bar (all enabled filters; never overflow-hidden)
 */

"use client";

import { useDebouncedCallback } from "@/lib/hooks/useDebounce";
import { cn } from "@/lib/utils/general-utils";
import { Search as MagnifyingGlassIcon } from "lucide-react";
import * as React from "react";
import { DeleteButton } from "./delete-button";
import { FilterVisibilityMenu } from "./filter-visibility-menu";
import type { TableColumn } from "./table-settings-menu";
import { TableSettingsMenu } from "./table-settings-menu";
import { TableFilterBar, type AvailableFilter } from "./table-filter-bar";

export type { AvailableFilter };

export interface BulkAction {
  /** Unique identifier for the action */
  id: string;
  /** Display label */
  label: string;
  /** Icon component (optional) */
  icon?: React.ReactNode;
  /** Handler function - receives array of selected row data */
  onClick: () => void;
  /** Variant for styling */
  variant?: "default" | "danger";
  /** Whether the action is currently loading */
  isLoading?: boolean;
  /** Whether the action is disabled */
  disabled?: boolean;
}

const { useCallback, useMemo, useState, useRef, useEffect } = React;

export interface EnhancedTableToolbarProps {
  /** Current search value */
  searchValue?: string;
  /** Search change handler */
  onSearchChange?: (_value: string) => void;
  /** Search input placeholder */
  searchPlaceholder?: string;
  /** Searchable fields for smart search */
  searchFields?: Array<{ id: string; label: string }>;
  /** Recent searches */
  recentSearches?: string[];
  /** Clear recent searches */
  onClearRecentSearches?: () => void;
  /**
   * @deprecated Quick filters have been replaced by FilterDropdown components.
   * This prop is accepted for backward compatibility but no longer renders pills.
   */
  quickFilters?: unknown[];
  /**
   * @deprecated Use FilterDropdown components in availableFilters instead.
   */
  activeQuickFilter?: string;
  /**
   * @deprecated Use FilterDropdown components in availableFilters instead.
   */
  onQuickFilterChange?: (_filterId: string) => void;
  /** Available filters that can be enabled/disabled */
  availableFilters?: AvailableFilter[];
  /** IDs of currently enabled filters */
  enabledFilters?: string[];
  /** IDs of filters that currently have a value applied */
  appliedFilterIds?: string[];
  /** Handler to enable/disable a single filter */
  onToggleFilterVisibility?: (_filterId: string, _enabled: boolean) => void;
  /** Handler to enable or disable every filter */
  onSetAllFilterVisibility?: (_enabled: boolean) => void;
  /** Advanced filters content (deprecated - use availableFilters instead) */
  advancedFiltersContent?: React.ReactNode;
  /** Custom action buttons (e.g., Add, Export) */
  children?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Show search */
  showSearch?: boolean;
  /**
   * @deprecated Quick filter pills have been removed. This prop is ignored.
   */
  showQuickFilters?: boolean;
  /** Show advanced filters toggle button */
  showAdvancedFilters?: boolean;
  /** Number of selected rows */
  selectedCount?: number;
  /** Callback to clear selection */
  onClearSelection?: () => void;
  /** Bulk actions to display when rows are selected */
  bulkActions?: BulkAction[];
  /** Show column selector (TableSettingsMenu) in the toolbar */
  showColumnSelector?: boolean;
  /** Columns for the column selector */
  columnSelectorColumns?: TableColumn[];
  /** Handler for column visibility changes from the column selector */
  onColumnVisibilityChange?: (_columnId: string, _visible: boolean) => void;
  /** Enable column reordering reset option in the column selector */
  enableColumnReordering?: boolean;
  /** Handler for resetting column order */
  onResetColumnOrder?: () => void;
  /** Handler for reordering movable columns in the column selector */
  onColumnOrderChange?: (_orderedIds: string[]) => void;
  /** Set a column's sort from the column selector */
  onColumnSortChange?: (
    _columnId: string,
    _direction: false | "asc" | "desc",
  ) => void;
  /** Page title rendered inline in the toolbar row */
  title?: string;
  /** Action element rendered on the far right (e.g. "New" button) */
  titleAction?: React.ReactNode;
  /** Always-visible control rendered at the start of the right-aligned actions cluster */
  leadingContent?: React.ReactNode;
}

export function EnhancedTableToolbar({
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Search by name, description, or creator...",
  availableFilters = [],
  enabledFilters = [],
  appliedFilterIds = [],
  onToggleFilterVisibility,
  onSetAllFilterVisibility,
  advancedFiltersContent: _advancedFiltersContent,
  children,
  className = "",
  showSearch = true,
  showAdvancedFilters = false,
  selectedCount = 0,
  onClearSelection,
  bulkActions = [],
  showColumnSelector = false,
  columnSelectorColumns,
  onColumnVisibilityChange,
  enableColumnReordering,
  onResetColumnOrder,
  onColumnOrderChange,
  onColumnSortChange,
  title,
  titleAction,
  leadingContent,
}: Readonly<EnhancedTableToolbarProps>) {
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const enabledFilterComponents = useMemo(
    () => availableFilters.filter((f) => enabledFilters.includes(f.id)),
    [availableFilters, enabledFilters],
  );

  const visibilityItems = useMemo(
    () => availableFilters.map(({ id, label }) => ({ id, label })),
    [availableFilters],
  );

  const debouncedOnSearchChange = useDebouncedCallback((value: string) => {
    onSearchChange?.(value);
  }, 300);

  useEffect(() => {
    setLocalSearchValue(searchValue);
  }, [searchValue]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalSearchValue(value);
      debouncedOnSearchChange(value);
    },
    [debouncedOnSearchChange],
  );

  const handleClearSearch = useCallback(() => {
    setLocalSearchValue("");
    debouncedOnSearchChange("");
    searchInputRef.current?.focus();
  }, [debouncedOnSearchChange]);

  const handleToggle = useCallback(
    (filterId: string, enabled: boolean) => {
      onToggleFilterVisibility?.(filterId, enabled);
    },
    [onToggleFilterVisibility],
  );

  return (
    <div className={cn(className)}>
      <div className="py-2 flex items-center gap-3">
        {title && (
          <h2 className="text-sm font-semibold text-gray-900 shrink-0">
            {title}
          </h2>
        )}

        {showSearch && (
          <div className="flex-shrink-0 w-56" data-tour="search-input">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                <MagnifyingGlassIcon
                  className="h-4 w-4 text-gray-400"
                  aria-hidden="true"
                />
              </div>

              <input
                ref={searchInputRef}
                type="search"
                value={localSearchValue}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                autoComplete="off"
                name="table-search-input"
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="none"
                className={cn(
                  "block w-full h-8 pl-8 pr-8 text-sm border rounded-md transition-all",
                  "placeholder:text-gray-400 text-gray-900",
                  "focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300",
                  "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none",
                  "border-gray-200 bg-white hover:border-gray-300",
                )}
                aria-label="Search"
              />

              <div className="absolute inset-y-0 right-0 pr-1.5 flex items-center gap-1">
                {localSearchValue && (
                  <button
                    onClick={handleClearSearch}
                    className="p-0.5 rounded hover:bg-gray-100 transition-colors"
                    type="button"
                    aria-label="Clear search"
                  >
                    <svg
                      className="h-3.5 w-3.5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <div
          className="flex items-center gap-2 shrink-0 ml-auto"
          data-testid="table-toolbar-actions"
        >
          {leadingContent ? (
            <>
              <div className="shrink-0">{leadingContent}</div>
              <div className="h-5 w-px bg-border shrink-0" aria-hidden="true" />
            </>
          ) : null}
          {selectedCount > 0 && (
            <div className="flex items-center gap-3 pr-3 border-r border-gray-200">
              <span className="text-sm font-medium text-gray-700">
                {selectedCount} {selectedCount === 1 ? "item" : "items"}{" "}
                selected
              </span>
              {onClearSelection && (
                <button
                  type="button"
                  onClick={onClearSelection}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Clear selection
                </button>
              )}
              {bulkActions.length > 0 && (
                <div className="flex items-center gap-2">
                  {bulkActions.map((action) =>
                    action.variant === "danger" ? (
                      <DeleteButton
                        key={action.id}
                        onClick={action.onClick}
                        isLoading={action.isLoading}
                        disabled={action.disabled}
                      >
                        {action.label}
                      </DeleteButton>
                    ) : (
                      <button
                        key={action.id}
                        type="button"
                        onClick={action.onClick}
                        disabled={action.disabled || action.isLoading}
                        className={cn(
                          "inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors",
                          "disabled:opacity-50 disabled:cursor-not-allowed",
                          "bg-white text-gray-700 border-gray-300 hover:bg-gray-50",
                        )}
                      >
                        {action.isLoading ? (
                          <svg
                            className="animate-spin h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        ) : (
                          action.icon
                        )}
                        {action.label}
                      </button>
                    ),
                  )}
                </div>
              )}
            </div>
          )}

          {showAdvancedFilters && availableFilters.length > 0 && (
            <FilterVisibilityMenu
              filters={visibilityItems}
              enabledIds={enabledFilters}
              appliedIds={appliedFilterIds}
              onToggle={handleToggle}
              onSetAll={onSetAllFilterVisibility}
            />
          )}

          {showColumnSelector &&
            columnSelectorColumns &&
            onColumnVisibilityChange && (
              <TableSettingsMenu
                columns={columnSelectorColumns}
                onColumnVisibilityChange={onColumnVisibilityChange}
                enableColumnReordering={enableColumnReordering}
                onResetColumnOrder={onResetColumnOrder}
                onColumnOrderChange={onColumnOrderChange}
                onColumnSortChange={onColumnSortChange}
              />
            )}

          {children}

          {titleAction}
        </div>
      </div>

      <TableFilterBar filters={enabledFilterComponents} />
    </div>
  );
}

export interface TableToolbarProps {
  /** Current search value */
  searchValue?: string;
  /** Search change handler */
  onSearchChange?: (_value: string) => void;
  /** Search input placeholder */
  searchPlaceholder?: string;
  /** Filter button click handler */
  onFilterClick?: () => void;
  /** Whether filters are currently visible */
  filtersVisible?: boolean;
  /** Custom action buttons (e.g., Add, Export) */
  children?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Show search input */
  showSearch?: boolean;
  /** Show filter button */
  showFilters?: boolean;
}

/**
 * Legacy TableToolbar - maintained for backward compatibility
 * @deprecated Use EnhancedTableToolbar for new implementations
 */
export function TableToolbar(props: Readonly<TableToolbarProps>) {
  const [enabledFilters] = React.useState<string[]>(
    props.filtersVisible ? ["legacy"] : [],
  );

  return (
    <EnhancedTableToolbar
      searchValue={props.searchValue}
      onSearchChange={props.onSearchChange}
      searchPlaceholder={props.searchPlaceholder}
      availableFilters={[
        {
          id: "legacy",
          label: "All Filters",
          component: <></>,
        },
      ]}
      enabledFilters={enabledFilters}
      onToggleFilterVisibility={
        props.onFilterClick
          ? () => {
              props.onFilterClick?.();
            }
          : undefined
      }
      className={props.className}
      showSearch={props.showSearch ?? true}
      showAdvancedFilters={props.showFilters ?? true}
    >
      {props.children}
    </EnhancedTableToolbar>
  );
}
